/**
 * Command K — mapping the existing command registry onto palette items.
 *
 * `CommandRegistryService` is fed by five services already (map, schedule,
 * article, catering, admin-tabs) through a `register/registerAll → disposer`
 * contract. None of them changes for the palette: this file adapts what they
 * already produce.
 *
 * Pure. Takes the registry's output, returns `CmdkItem`s.
 */

import type {
  AppContext, Command, CommandRegistryService,
} from '../../../../../../core/services/command-registry.service';
import type { AppSection } from '../../../../../../core/services/shell-state.service';
import type { CmdkActionItem, CmdkNavItem } from '../../models/cmdk.model';
import type { CmdkProviderContext } from '../../models/cmdk-provider';

/**
 * Registry groups that are navigation rather than action. Everything else the
 * registry emits is treated as an action, which is the safe default: a new
 * contributor's command shows up under Actions rather than vanishing.
 */
const NAV_GROUPS = new Set(['navigation']);

export function isNavCommand(command: Command): boolean {
  return NAV_GROUPS.has(command.group);
}

/**
 * Registry command ids that open an in-palette flow rather than running their
 * `execute()`. The registry's own handlers navigate or open a dialog, which is
 * right from a keyboard shortcut and wrong from inside a palette that can host
 * the flow itself.
 *
 * An id absent from this map keeps its original `execute()`, so a contributor
 * who knows nothing about the palette still gets working behaviour.
 */
const FLOW_BY_COMMAND_ID: Readonly<Record<string, string>> = {
  'action:book-meeting': 'book-room',
  'action:find-time': 'find-a-time',
  'action:quick-book': 'quick-book',
  'action:report-issue': 'report-issue',
  'nav:keyboard-shortcuts': 'help',
};

export function flowForCommand(command: Command): string | undefined {
  return FLOW_BY_COMMAND_ID[command.id];
}

/**
 * The navigation title stays verbatim — "Go to Map", not "Map".
 *
 * It reads redundant under a "Go to" header, and stripping it was the first
 * instinct. But the title is also what the ranker matches against, and the
 * subsequence tier only looks at the title: with the prefix stripped, `gtm`
 * stops finding "Go to Map". Aliases do not rescue it, because a subsequence
 * over an alias would highlight characters the row does not display.
 *
 * The design's own reference rows read "Go to My Week" for the same reason.
 */
export function toNavItem(command: Command): CmdkNavItem {
  return {
    id: command.id,
    kind: 'nav',
    title: command.title,
    subtitle: command.subtitle,
    aliases: command.aliases,
    command,
    shortcut: command.shortcut,
  };
}

export function toActionItem(command: Command): CmdkActionItem {
  return {
    id: command.id,
    kind: 'action',
    title: command.title,
    subtitle: command.subtitle,
    aliases: command.aliases,
    command,
    shortcut: command.shortcut,
    flowId: flowForCommand(command),
  };
}

/**
 * The visible command catalogue — NOT the registry's own search results.
 *
 * This distinction is the whole reason the function exists.
 * `CommandRegistryService.search(q)` applies its own substring ladder before
 * returning anything, so asking it to match means the palette's ranker only
 * ever sees rows the weaker matcher already accepted. Concretely: `gtm` scores
 * 0 in `scoreMatch`, so "Go to Map" is filtered out before the subsequence
 * tier — which exists precisely to catch it — is ever consulted.
 *
 * So the provider takes the CATALOGUE and does its own matching. What the
 * registry is still authoritative for is VISIBILITY: each command's
 * `visible(ctx)` predicate carries the capability and action gating that the
 * five contributors declared (`isWorkspaceVisible`, the `article:create`
 * gate), and that must be honoured exactly.
 *
 * `primaryOnly` mirrors the registry's own empty-query rule: secondary
 * commands (Welcome tour, What's new, Keyboard shortcuts) are findable by
 * name but do not clutter the idle list.
 */
export function visibleCommands(
  registry: CommandRegistryService,
  ctx: CmdkProviderContext,
  options: { readonly primaryOnly: boolean },
): Command[] {
  const appContext: AppContext = {
    currentSection: ctx.section as AppSection,
    userActions: [...ctx.userActions],
  };
  return registry
    .commands()
    .filter(command => !command.visible || command.visible(appContext))
    .filter(command => !options.primaryOnly || command.primary !== false);
}

/**
 * The registry's `contextBoost` as a palette boost. Same clamp as
 * `registryBoost`, applied to the context signal rather than to a score that
 * already has text matching baked into it.
 */
export function contextBoostFor(command: Command, ctx: CmdkProviderContext): number {
  const appContext: AppContext = {
    currentSection: ctx.section as AppSection,
    userActions: [...ctx.userActions],
  };
  const raw = command.contextBoost?.(appContext) ?? 0;
  // The registry's own boosts run to ~100 (`command-registry.service.ts:174`
  // uses 15, others go higher); the palette's window is ±20.
  return Math.max(-20, Math.min(20, raw / 5));
}
